import { Component } from '@theme/component';
import { convertMoneyToMinorUnits, formatMoney } from '@theme/money-formatting';

const CEP_STORAGE_KEY = 'brizze-sacola-cep';
const FRETE_STORAGE_KEY = 'brizze-sacola-frete-centavos';
const FRETE_ABERTO_KEY = 'brizze-sacola-frete-aberto';

/** @typedef {object} FreteRule
 * @property {string[]} prefixes
 * @property {number} minimum
 */

/** @typedef {object} Refs
 * @property {HTMLFormElement} cepForm
 * @property {HTMLInputElement} cep
 * @property {HTMLButtonElement} cepSubmit
 * @property {HTMLElement} cepEditWrap
 * @property {HTMLElement} cepQuotedWrap
 * @property {HTMLElement} cepDisplayed
 * @property {HTMLButtonElement} alterarCep
 * @property {HTMLButtonElement} cepResubmit
 * @property {HTMLElement} cepErro
 * @property {HTMLElement} progressWrap
 * @property {HTMLElement} progressFill
 * @property {HTMLElement} progressIcon
 * @property {HTMLElement} progressMessage
 * @property {HTMLElement} freteValor
 * @property {HTMLElement} freteOpcoes
 */

/**
 * Sacola: barra de frete grátis, cotação de CEP e total com frete.
 * @extends {Component<Refs>}
 */
class CartDrawerBrizze extends Component {
  requiredRefs = [
    'cepForm',
    'cep',
    'cepSubmit',
    'cepEditWrap',
    'cepQuotedWrap',
    'cepDisplayed',
    'alterarCep',
    'cepResubmit',
    'cepErro',
    'progressWrap',
    'progressFill',
    'progressIcon',
    'progressMessage',
    'freteValor',
    'freteOpcoes',
  ];

  /** @type {AbortController | undefined} */
  #activeFetch;

  /** @type {number} */
  #freteCentavos = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.cepForm.addEventListener('submit', this.#onCepSubmit);
    this.refs.cep.addEventListener('input', this.#onCepInput);
    this.refs.alterarCep.addEventListener('click', this.#onAlterarCep);

    this.#restoreSessionState();
    this.#syncUiFromCart();

    if (sessionStorage.getItem(FRETE_ABERTO_KEY) === '1' && sessionStorage.getItem(CEP_STORAGE_KEY)) {
      this.#showQuotedState(sessionStorage.getItem(CEP_STORAGE_KEY) || '');
      this.#consultarFrete(false);
    }
  }

  updatedCallback() {
    super.updatedCallback();
    this.#restoreSessionState();
    this.#syncUiFromCart();

    if (sessionStorage.getItem(FRETE_ABERTO_KEY) === '1' && sessionStorage.getItem(CEP_STORAGE_KEY)) {
      this.#showQuotedState(sessionStorage.getItem(CEP_STORAGE_KEY) || '');
    }
  }

  #restoreSessionState() {
    const storedFrete = sessionStorage.getItem(FRETE_STORAGE_KEY);
    if (storedFrete != null && storedFrete !== '') {
      const parsed = parseInt(storedFrete, 10);
      if (!Number.isNaN(parsed)) {
        this.#freteCentavos = parsed;
      }
    }

    const storedCep = sessionStorage.getItem(CEP_STORAGE_KEY);
    if (storedCep && this.refs.cep) {
      this.refs.cep.value = storedCep;
    }
  }

  #syncUiFromCart() {
    this.#updateProgressBar();
    if (sessionStorage.getItem(FRETE_STORAGE_KEY) !== null) {
      this.#renderFreteLine();
    }
  }

  disconnectedCallback() {
    this.refs.cepForm.removeEventListener('submit', this.#onCepSubmit);
    this.refs.cep.removeEventListener('input', this.#onCepInput);
    this.refs.alterarCep.removeEventListener('click', this.#onAlterarCep);
    this.#activeFetch?.abort();
    super.disconnectedCallback();
  }

  #onAlterarCep = () => {
    sessionStorage.removeItem(FRETE_ABERTO_KEY);
    sessionStorage.removeItem(FRETE_STORAGE_KEY);
    this.#freteCentavos = 0;
    this.classList.remove('brizze-sacola--frete-aberto');

    this.refs.freteOpcoes.hidden = true;
    this.refs.freteOpcoes.innerHTML = '';

    this.#showEditState();
    this.refs.freteValor.textContent = '—';
    this.refs.freteValor.classList.remove('brizze-sacola__frete-gratis');
    this.#updateGrandTotal();

    this.refs.cep.focus();
  };

  #showEditState() {
    this.refs.cepEditWrap.hidden = false;
    this.refs.cepQuotedWrap.hidden = true;
  }

  /** @param {string} cepMasked */
  #showQuotedState(cepMasked) {
    this.refs.cepDisplayed.textContent = cepMasked;
    this.refs.cepEditWrap.hidden = true;
    this.refs.cepQuotedWrap.hidden = false;
  }

  #onCepSubmit = (event) => {
    event.preventDefault();
    this.#consultarFrete(true);
  };

  #onCepInput = () => {
    const digits = this.#cepDigits(this.refs.cep.value);
    const masked = digits.length <= 5 ? digits : `${digits.slice(0, 5)}-${digits.slice(5, 8)}`;
    if (this.refs.cep.value !== masked) this.refs.cep.value = masked;
    if (digits.length === 8) {
      sessionStorage.setItem(CEP_STORAGE_KEY, masked);
      this.#updateProgressBar();
    }
  };

  #cepDigits(value) {
    return value.replace(/\D/g, '').slice(0, 8);
  }

  /** @returns {FreteRule[]} */
  #readRules() {
    const script = this.querySelector('[data-sacola-regras]');
    if (!(script instanceof HTMLScriptElement)) return [];
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** @param {string} cepDigits */
  #matchMinimum(cepDigits) {
    const defaultMinimum = parseInt(this.dataset.defaultMinimum || '0', 10) || 0;
    const rules = this.#readRules();
    let fallback = defaultMinimum;

    for (const rule of rules) {
      const minimum = Number(rule.minimum) || 0;
      const prefixes = (rule.prefixes || [])
        .map((p) => String(p).trim().replace(/\D/g, ''))
        .filter(Boolean);

      if (prefixes.length === 0) {
        fallback = minimum || fallback;
        continue;
      }

      for (const prefix of prefixes) {
        if (cepDigits.startsWith(prefix)) {
          return minimum || defaultMinimum;
        }
      }
    }

    return fallback || defaultMinimum;
  }

  #updateProgressBar() {
    const cartAmount =
      parseInt(this.dataset.total || '0', 10) || parseInt(this.dataset.subtotal || '0', 10) || 0;
    const cepDigits = this.#cepDigits(this.refs.cep?.value || sessionStorage.getItem(CEP_STORAGE_KEY) || '');
    const minimum =
      cepDigits.length === 8
        ? this.#matchMinimum(cepDigits)
        : parseInt(this.dataset.defaultMinimum || '0', 10) || 0;

    if (minimum <= 0) {
      this.refs.progressWrap.hidden = true;
      return;
    }

    this.refs.progressWrap.hidden = false;
    const progress = Math.min(100, Math.round((cartAmount / minimum) * 100));
    const remaining = Math.max(0, minimum - cartAmount);

    this.refs.progressFill.style.width = `${progress}%`;
    this.refs.progressIcon.style.left = `clamp(0px, calc(${progress}% - 16px), calc(100% - 32px))`;

    const moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';
    const currency = this.dataset.currency || 'BRL';
    const faltaTemplate = this.dataset.textoFalta || 'Faltam apenas [valor] para ter FRETE GRÁTIS';
    const atingido = this.dataset.textoAtingido || 'Você ganhou FRETE GRÁTIS';

    if (remaining <= 0) {
      this.refs.progressMessage.textContent = atingido;
    } else {
      const valor = formatMoney(remaining, moneyFormat, currency);
      this.refs.progressMessage.textContent = faltaTemplate.replace('[valor]', valor);
    }
  }

  #setCepErro(message) {
    if (!message) {
      this.refs.cepErro.hidden = true;
      this.refs.cepErro.textContent = '';
      return;
    }
    this.refs.cepErro.hidden = false;
    this.refs.cepErro.textContent = message;
  }

  #setCepLoading(loading) {
    this.refs.cepSubmit.disabled = loading;
    this.refs.cepResubmit.disabled = loading;
    this.refs.cepSubmit.setAttribute('aria-busy', loading ? 'true' : 'false');
    this.refs.cepResubmit.setAttribute('aria-busy', loading ? 'true' : 'false');
    this.classList.toggle('brizze-sacola--frete-carregando', loading);
  }

  async #fetchViaCep(cep) {
    const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`, {
      signal: this.#activeFetch?.signal,
    });
    if (!response.ok) throw new Error('viacep');
    const data = await response.json();
    if (data.erro) throw new Error('cep_not_found');
    return data;
  }

  /** @param {URLSearchParams} params */
  async #fetchShippingRates(params) {
    const base = Theme.routes.cart_url;
    const query = params.toString();
    const pollDelayMs = 400;
    const maxAttempts = 15;

    const prepareResponse = await fetch(`${base}/prepare_shipping_rates.json?${query}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      signal: this.#activeFetch?.signal,
    });
    if (!prepareResponse.ok) throw new Error('rates_failed');

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const response = await fetch(`${base}/async_shipping_rates.json?${query}`, {
        headers: { Accept: 'application/json' },
        signal: this.#activeFetch?.signal,
      });

      if (response.ok) {
        const payload = await response.json();
        if (payload.shipping_rates?.length) return payload.shipping_rates;
        if (payload.error) throw new Error(payload.error);
      }

      await new Promise((resolve) => window.setTimeout(resolve, pollDelayMs));
    }

    const fallback = await fetch(`${base}/shipping_rates.json?${query}`, {
      headers: { Accept: 'application/json' },
      signal: this.#activeFetch?.signal,
    });
    if (fallback.ok) {
      const payload = await fallback.json();
      if (payload.shipping_rates?.length) return payload.shipping_rates;
    }

    throw new Error('rates_timeout');
  }

  /** @param {Record<string, unknown>} rate */
  #formatPrazo(rate) {
    const days = rate.delivery_days;
    if (Array.isArray(days) && days.length > 0) {
      const nums = days.map((d) => Number(d)).filter((n) => !Number.isNaN(n));
      if (nums.length) {
        const min = Math.min(...nums);
        const max = Math.max(...nums);
        const avg = Math.round((min + max) / 2);
        return `Em média ${avg} dia(s) úteis`;
      }
    }

    if (rate.delivery_date && typeof rate.delivery_date === 'string') {
      const date = new Date(rate.delivery_date);
      if (!Number.isNaN(date.getTime())) {
        return `Previsto para ${date.toLocaleDateString('pt-BR')}`;
      }
    }

    if (typeof rate.description === 'string' && rate.description.trim()) {
      return rate.description.trim();
    }

    return '';
  }

  /** @param {Record<string, unknown>} rate */
  #ratePriceMinor(rate) {
    const priceNum = parseFloat(String(rate.price ?? '0'));
    const currency = this.dataset.currency || 'BRL';
    if (Number.isNaN(priceNum)) return 0;
    return convertMoneyToMinorUnits(String(priceNum), currency) ?? Math.round(priceNum * 100);
  }

  /** @param {Record<string, unknown>} rate */
  #formatRatePrice(rate) {
    const moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';
    const currency = this.dataset.currency || 'BRL';
    const gratisLabel = this.dataset.textoGratis || 'Grátis';
    const minor = this.#ratePriceMinor(rate);
    if (minor === 0) return gratisLabel;
    return formatMoney(minor, moneyFormat, currency);
  }

  /** @param {Record<string, unknown>[]} rates */
  #renderRatesList(rates) {
    const selectedMinor = sessionStorage.getItem(FRETE_STORAGE_KEY);
    this.refs.freteOpcoes.innerHTML = '';

    rates.forEach((rate, index) => {
      const minor = this.#ratePriceMinor(rate);
      const nome = String(rate.presentment_name || rate.name || '');
      const preco = this.#formatRatePrice(rate);
      const prazo = this.#formatPrazo(rate);

      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'brizze-sacola__frete-opcao';
      btn.setAttribute('role', 'listitem');
      btn.dataset.freteMinor = String(minor);
      btn.dataset.freteIndex = String(index);
      if (selectedMinor === String(minor)) {
        btn.classList.add('brizze-sacola__frete-opcao--selected');
      }

      const main = document.createElement('span');
      main.className = 'brizze-sacola__frete-opcao-main';
      const nomeEl = document.createElement('strong');
      nomeEl.textContent = nome;
      const precoEl = document.createElement('strong');
      precoEl.textContent = preco;
      main.append(nomeEl, document.createTextNode(' • '), precoEl);
      btn.appendChild(main);

      if (prazo) {
        const prazoEl = document.createElement('span');
        prazoEl.className = 'brizze-sacola__frete-opcao-prazo';
        prazoEl.textContent = ` – ${prazo}`;
        btn.appendChild(prazoEl);
      }

      btn.addEventListener('click', () => this.#selectRate(minor, btn));
      this.refs.freteOpcoes.appendChild(btn);
    });

    this.refs.freteOpcoes.hidden = rates.length === 0;
    this.classList.add('brizze-sacola--frete-aberto');
  }

  /** @param {number} minor @param {HTMLButtonElement} button */
  #selectRate(minor, button) {
    this.#freteCentavos = minor;
    sessionStorage.setItem(FRETE_STORAGE_KEY, String(minor));

    this.refs.freteOpcoes.querySelectorAll('.brizze-sacola__frete-opcao').forEach((el) => {
      el.classList.remove('brizze-sacola__frete-opcao--selected');
    });
    button.classList.add('brizze-sacola__frete-opcao--selected');
    this.#renderFreteLine();
  }

  #renderFreteLine() {
    const moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';
    const currency = this.dataset.currency || 'BRL';

    if (sessionStorage.getItem(FRETE_STORAGE_KEY) === null) {
      this.refs.freteValor.textContent = '—';
      this.refs.freteValor.classList.remove('brizze-sacola__frete-gratis');
      this.#updateGrandTotal();
      return;
    }

    if (this.#freteCentavos === 0) {
      this.refs.freteValor.textContent = 'GRÁTIS';
      this.refs.freteValor.classList.add('brizze-sacola__frete-gratis');
    } else {
      this.refs.freteValor.textContent = formatMoney(this.#freteCentavos, moneyFormat, currency);
      this.refs.freteValor.classList.remove('brizze-sacola__frete-gratis');
    }

    this.#updateGrandTotal();
  }

  #updateGrandTotal() {
    const cartTotal = parseInt(this.dataset.total || '0', 10) || 0;
    const frete =
      sessionStorage.getItem(FRETE_STORAGE_KEY) !== null ? this.#freteCentavos : 0;
    const grand = cartTotal + frete;
    const moneyFormat = this.dataset.moneyFormat || '{{amount_with_comma_separator}}';
    const currency = this.dataset.currency || 'BRL';
    const formatted = formatMoney(grand, moneyFormat, currency);

    const totalEl = this.querySelector('[data-sacola-total]');
    if (totalEl) {
      totalEl.textContent = formatted;
      if (totalEl instanceof HTMLElement && 'value' in totalEl) {
        totalEl.setAttribute('value', formatted);
      }
    }
  }

  /** @param {boolean} showErrors */
  async #consultarFrete(showErrors) {
    if (this.#activeFetch) this.#activeFetch.abort();
    this.#activeFetch = new AbortController();

    this.#setCepErro('');
    const cepSource =
      this.refs.cepQuotedWrap.hidden === false
        ? sessionStorage.getItem(CEP_STORAGE_KEY) || this.refs.cep.value
        : this.refs.cep.value;
    const cep = this.#cepDigits(cepSource);

    if (cep.length !== 8) {
      if (showErrors) {
        this.#setCepErro(this.dataset.msgCepInvalido || 'Informe um CEP válido com 8 dígitos.');
      }
      return;
    }

    const cepMasked = cep.length <= 5 ? cep : `${cep.slice(0, 5)}-${cep.slice(5)}`;
    sessionStorage.setItem(CEP_STORAGE_KEY, cepMasked);
    this.refs.cep.value = cepMasked;
    this.#updateProgressBar();
    this.#setCepLoading(true);

    try {
      const viaCep = await this.#fetchViaCep(cep);
      const uf = viaCep.uf;
      if (!uf) throw new Error('cep_not_found');

      const params = new URLSearchParams({
        'shipping_address[zip]': cepMasked,
        'shipping_address[country]': 'Brazil',
        'shipping_address[province]': uf,
      });
      if (viaCep.localidade) {
        params.set('shipping_address[city]', viaCep.localidade);
      }

      const rates = await this.#fetchShippingRates(params);
      if (!rates.length) {
        if (showErrors) {
          this.#setCepErro(this.dataset.msgSemOpcoes || 'Nenhuma opção de entrega para este CEP.');
        }
        return;
      }

      if (showErrors) {
        sessionStorage.removeItem(FRETE_STORAGE_KEY);
        this.#freteCentavos = 0;
        this.refs.freteValor.textContent = '—';
        this.refs.freteValor.classList.remove('brizze-sacola__frete-gratis');
        this.#updateGrandTotal();
      }

      sessionStorage.setItem(FRETE_ABERTO_KEY, '1');
      this.#showQuotedState(cepMasked);
      this.#renderRatesList(rates);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      if (showErrors) {
        if (error instanceof Error && error.message === 'cep_not_found') {
          this.#setCepErro(this.dataset.msgCepNaoEncontrado || 'CEP não encontrado.');
        } else {
          this.#setCepErro(this.dataset.msgErroGenerico || 'Não foi possível calcular o frete.');
        }
      }
    } finally {
      this.#setCepLoading(false);
    }
  }
}

if (!customElements.get('cart-drawer-brizze')) {
  customElements.define('cart-drawer-brizze', CartDrawerBrizze);
}
